import PageHeader from "@/components/site/PageHeader";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Globe, Instagram, ArrowRight } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { value: "audit", label: "Revenue Leak Audit" },
  { value: "intensive", label: "Strategy Intensive" },
  { value: "advisory", label: "Monthly Advisory" },
  { value: "pm_collab", label: "Property Manager Collaboration" },
  { value: "general", label: "General Inquiry" },
];

export default function Contact() {
  usePageTitle("Contact");
  const [params] = useSearchParams();
  const initialType = params.get("type") || "audit";

  const [form, setForm] = useState({
    name: "",
    email: "",
    property_url: "",
    inquiry_type: initialType,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, inquiry_type: initialType }));
  }, [initialType]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thank you — your note is in.");
      setForm({ name: "", email: "", property_url: "", inquiry_type: "audit", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email hello@sarahatkinson.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-ivory">
      <PageHeader
        overline="Let's Talk"
        title="Strategy starts with"
        accent="a conversation."
        description="Tell me where you are, what you're noticing, and what you're hoping to feel about your business. I read every message personally."
        testId="contact-header"
      />

      <section className="pb-24 lg:pb-32">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Form */}
          <div className="lg:col-span-7">
            {sent ? (
              <div data-testid="contact-success" className="bg-cream border border-sage/40 p-12 lg:p-16">
                <p className="overline mb-4">Thank you.</p>
                <p className="font-serif text-3xl md:text-4xl text-navy leading-snug mb-5">
                  Your note is in — and <em className="font-serif italic text-sage">read personally.</em>
                </p>
                <p className="font-sans text-charcoal/70 leading-relaxed">
                  I'll be in touch within 1–2 business days with a calm, considered next step. If you'd like to reach me directly, you're welcome to write me at <a href="mailto:hello@sarahatkinsonadvisory.com" className="text-navy underline underline-offset-4 decoration-gold/70">hello@sarahatkinsonadvisory.com</a>.
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another"
                  className="editorial-link mt-8"
                >
                  Send another message <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="overline block mb-3">Your Name</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="First & last"
                      data-testid="contact-name"
                      className="input-editorial"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-3">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      data-testid="contact-email"
                      className="input-editorial"
                    />
                  </div>
                </div>

                <div>
                  <label className="overline block mb-3">Property / Listing URL (optional)</label>
                  <input
                    type="url"
                    value={form.property_url}
                    onChange={update("property_url")}
                    placeholder="https://airbnb.com/rooms/..."
                    data-testid="contact-property-url"
                    className="input-editorial"
                  />
                </div>

                <div>
                  <label className="overline block mb-4">What brings you here?</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {INQUIRY_TYPES.map((t) => (
                      <label
                        key={t.value}
                        data-testid={`contact-type-${t.value}`}
                        className={`cursor-pointer border px-5 py-4 transition-all duration-300 ${
                          form.inquiry_type === t.value
                            ? "border-navy bg-navy text-ivory"
                            : "border-warmGray/30 hover:border-navy/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="inquiry_type"
                          value={t.value}
                          checked={form.inquiry_type === t.value}
                          onChange={update("inquiry_type")}
                          className="sr-only"
                        />
                        <span className="font-ui uppercase tracking-[0.14em] text-[11px]">{t.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="overline block mb-3">Tell me a little more</label>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Where you are, what you're noticing, what you're hoping for..."
                    data-testid="contact-message"
                    className="input-editorial resize-none"
                  />
                </div>

                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary">
                  {loading ? "Sending..." : (<>Send Message <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>
            )}
          </div>

          {/* Side */}
          <aside className="lg:col-span-5">
            <div className="bg-navy text-ivory p-10 lg:p-12">
              <p className="overline text-gold mb-4">Other ways to reach me</p>
              <h3 className="font-serif text-2xl md:text-3xl leading-snug mb-8">
                Calm, considered conversations — never a sales pitch.
              </h3>
              <ul className="space-y-5 font-sans text-sm">
                <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-gold" /> hello@sarahatkinsonadvisory.com</li>
                <li className="flex items-center gap-3"><Globe className="w-4 h-4 text-gold" /> sarahatkinsonadvisory.com</li>
                <li className="flex items-center gap-3"><Instagram className="w-4 h-4 text-gold" /> @sarahatkinsonadvisory</li>
              </ul>
              <span className="block w-12 h-px bg-gold mt-10 mb-6" />
              <p className="font-serif italic text-gold text-xl md:text-2xl leading-snug">Clarity creates confidence.</p>
              <p className="font-sans text-ivory/65 text-sm mt-2">Strategy creates freedom.</p>
            </div>

            <div className="mt-10 border-l-2 border-gold pl-6">
              <p className="overline mb-3">Response Time</p>
              <p className="font-sans text-charcoal/80 leading-relaxed">
                I read every message personally and respond within 1–2 business days with a calm, considered next step.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
